import type { ApiError, AuthTab, User } from "@/types";
import { api } from "@/api";
import { els, state } from "@/state";
import { toast } from "@/views/admin";
import { escapeHtml } from "@/utils";

function authNoticeElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>("#authNotice");
}

function authResendActions(): HTMLElement | null {
  return document.querySelector<HTMLElement>("#authResendActions");
}

function authResendButton(): HTMLButtonElement | null {
  return document.querySelector<HTMLButtonElement>("#authResendBtn");
}

function clearAuthNotice(): void {
  const notice = authNoticeElement();
  if (notice) {
    notice.classList.add("hidden");
    notice.innerHTML = "";
  }
  const actions = authResendActions();
  if (actions) actions.hidden = true;
  const resendBtn = authResendButton();
  if (resendBtn) delete resendBtn.dataset.email;
}

export function showAuthNotice(message: string, email = "", allowResend = false): void {
  const notice = authNoticeElement();
  if (notice) {
    notice.innerHTML = message;
    notice.classList.remove("hidden");
  }
  const actions = authResendActions();
  if (actions) actions.hidden = !allowResend;
  const resendBtn = authResendButton();
  if (resendBtn) {
    resendBtn.dataset.email = email;
    resendBtn.hidden = !allowResend;
  }
}

/** 切换登录/注册标签 */
export function setAuthTab(tab: AuthTab): void {
  state.authTab = tab;
  const isLogin = tab === "login";
  document.querySelectorAll<HTMLElement>("[data-auth-tab]").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.authTab === tab);
  });
  if (els.authNameLabel) els.authNameLabel.hidden = isLogin;
  if (els.authPasswordConfirmLabel) els.authPasswordConfirmLabel.hidden = isLogin;
  if (els.authSubmitBtn) els.authSubmitBtn.textContent = isLogin ? "登录" : "注册";
  if (els.authPassword) els.authPassword.placeholder = isLogin ? "密码" : "至少 8 位";
  if (els.authPassword) els.authPassword.autocomplete = isLogin ? "current-password" : "new-password";
  if (els.authPasswordConfirm) {
    els.authPasswordConfirm.required = !isLogin;
    if (isLogin) els.authPasswordConfirm.value = "";
  }
}

/** 渲染登录/注册页面 */
export function renderAuthPage(): void {
  const tab: AuthTab =
    state.route === "register" ? "register" : state.route === "login" ? "login" : state.authTab;
  setAuthTab(tab);
  clearAuthNotice();
  hydrateAuthStateFromLocation();
}

/** 在 auth 错误框中展示错误信息;登录失败时附带剩余尝试次数 */
export function showAuthError(errorBox: HTMLElement | null, error: ApiError): void {
  if (!errorBox) {
    toast(error.message, "error");
    return;
  }
  const details = error.details || {};
  let html = escapeHtml(error.message);

  const emailRemaining = details.emailRemaining;
  const ipRemaining = details.ipRemaining;

  if (typeof emailRemaining === "number" || typeof ipRemaining === "number") {
    const lines: string[] = [];
    if (typeof emailRemaining === "number" && emailRemaining < 5) {
      lines.push(`邮箱剩余尝试次数:${emailRemaining} / 5`);
    }
    if (typeof ipRemaining === "number" && ipRemaining < 20) {
      lines.push(`IP 剩余尝试次数:${ipRemaining} / 20`);
    }
    if (lines.length) {
      html += `<br><small>${lines.join(" · ")}</small>`;
      errorBox.classList.add("form-warning");
    }
  }

  errorBox.innerHTML = html;
  errorBox.classList.remove("hidden");
}

function hydrateAuthStateFromLocation(): void {
  const params = new URLSearchParams(window.location.search);
  const email = params.get("email")?.trim() || "";
  if (email && els.authEmail) {
    els.authEmail.value = email;
  }
  if (params.get("verified") === "1") {
    showAuthNotice(`邮箱验证成功${email ? `：<code>${escapeHtml(email)}</code>` : ""}，现在可以登录了。`);
    return;
  }
  if (params.get("pendingVerification") === "1") {
    showAuthNotice(
      `验证邮件已发送${email ? `：<code>${escapeHtml(email)}</code>` : ""}。请前往邮箱完成验证后再登录。`,
      email,
      true
    );
  }
}

export async function handleResendVerification(): Promise<void> {
  const resendBtn = authResendButton();
  const email = resendBtn?.dataset.email?.trim() || els.authEmail?.value.trim() || "";
  if (!email) {
    toast("请先填写邮箱", "error");
    return;
  }

  if (resendBtn) resendBtn.disabled = true;
  try {
    await api("/api/auth/verify-email/resend", {
      method: "POST",
      body: JSON.stringify({ email })
    });
    showAuthNotice(`验证邮件已重新发送：<code>${escapeHtml(email)}</code>。请前往邮箱查收。`, email, true);
    toast("验证邮件已发送", "success");
  } catch (error) {
    showAuthError(els.authError, error as ApiError);
  } finally {
    if (resendBtn) resendBtn.disabled = false;
  }
}

/** authForm submit 事件处理 */
export async function handleAuthSubmit(event: Event): Promise<void> {
  event.preventDefault();
  const email = els.authEmail?.value.trim() ?? "";
  const password = els.authPassword?.value ?? "";
  const passwordConfirm = els.authPasswordConfirm?.value ?? "";
  const name = els.authName?.value.trim() ?? "";
  const isLogin = state.authTab === "login";

  if (els.authError) {
    els.authError.classList.add("hidden");
    els.authError.classList.remove("form-warning");
  }
  clearAuthNotice();

  if (!isLogin && password !== passwordConfirm) {
    showAuthError(els.authError, Object.assign(new Error("两次输入的密码不一致"), {
      status: 400
    }) as ApiError);
    return;
  }

  try {
    const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";
    const payload = isLogin ? { email, password } : { email, password, name };
    const data = await api<{ member?: User; csrfToken?: string; email?: string; pendingVerification?: boolean }>(endpoint, {
      method: "POST",
      body: JSON.stringify(payload)
    });
    if (!isLogin) {
      const pendingEmail = data.email || email;
      toast("验证邮件已发送", "success");
      window.location.href = `/login/?pendingVerification=1&email=${encodeURIComponent(pendingEmail)}`;
      return;
    }
    state.user = data.member || null;
    state.csrfToken = data.csrfToken || "";
    toast("登录成功", "success");
    // 登录后进入独立的用户自主管理端页面。
    window.location.href = "/app/";
  } catch (error) {
    if ((error as ApiError).code === "EMAIL_NOT_VERIFIED") {
      showAuthNotice(`邮箱尚未验证：<code>${escapeHtml(email)}</code>。请先完成邮箱验证。`, email, true);
    }
    if ((error as ApiError).code === "EMAIL_PENDING_VERIFICATION") {
      showAuthNotice(`该邮箱仍待验证：<code>${escapeHtml(email)}</code>。可以重新发送验证邮件。`, email, true);
    }
    showAuthError(els.authError, error as ApiError);
  }
}
